/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconButton } from 'components/buttons/IconSVG';
import CustomDialog from 'components/modals/common/CustomDialog';
import { useMessageBar } from 'hooks/useMessageBar';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { MST_TGT_TYPES } from '../constants';
import { MstTgtTpCd, UtMatrixOption } from '../types';

/** 팝업이 돌려주는 비교 대상 1건 — 배열 순서가 SORT_ORD(1,2,3) */
export interface CompareTarget extends UtMatrixOption {
  /** TB_EELMB_UTM_RPT_UTM_M.MST_TGT_TP_CD */
  mstTgtTpCd: string;
  sortOrd: number;
}

/** 저장/표시 순서 — 기준 → 비교대상 → 참조 (그대로 SORT_ORD 1,2,3) */
const ROLE_ORDER: MstTgtTpCd[] = ['MST', 'TGT', 'REF'];

const PickerRoot = styled.div`
  font-size: 13px;
  color: #1f1f1f;

  /* 검색칸을 걷어내고 건수만 남긴 줄 */
  .picker-count {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 10px;
    font-size: 12px;
    color: #5b5c5b;
  }

  .picker-body {
    border: 1px solid #dde0df;
    border-radius: 3px;
    max-height: 320px;
    overflow: auto;
  }
  table {
    border-collapse: collapse;
    width: 100%;
  }
  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f1f4f3;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    padding: 7px 8px;
    border-right: 1px solid #dde0df;
    border-bottom: 1px solid #dde0df;
    white-space: nowrap;
  }
  thead th:last-child {
    border-right: none;
  }
  tbody td {
    padding: 6px 8px;
    font-size: 12px;
    border-right: 1px solid #f1f4f3;
    border-bottom: 1px solid #f1f4f3;
  }
  tbody td:last-child {
    border-right: none;
  }
  td.center {
    text-align: center;
  }
  .empty {
    padding: 40px 20px;
    text-align: center;
    color: #979998;
    font-size: 12px;
  }

  /* 역할이 지정된 행은 배경으로도 구분한다 (기준=파랑 / 비교대상=노랑 / 참조=회색) */
  tr.row-mst td {
    background: #eaf3fd;
  }
  tr.row-tgt td {
    background: #fdf7e3;
  }
  tr.row-ref td {
    background: #f4f5f6;
  }

  select.role-select {
    height: 28px;
    padding: 2px 6px;
    border: 1px solid #b9bcbb;
    border-radius: 2px;
    font-size: 12px;
    font-family: inherit;
    background: #fff;
    color: #1f1f1f;
  }
  select.role-select:focus {
    outline: none;
    border-color: #3ec2cf;
  }
  select.role-select.role-mst {
    border-color: #2d9bb2;
    color: #135678;
    font-weight: 700;
  }
  select.role-select.role-tgt {
    border-color: #e8a33d;
    color: #a15c00;
    font-weight: 700;
  }
  select.role-select.role-ref {
    border-color: #a897df;
    color: #554596;
    font-weight: 700;
  }
`;

type Props = {
  open: boolean;
  close: () => void;
  /** 후보 목록 (정합화 Report 목록 화면은 전체, Summary 화면은 선택된 행) */
  matrices: UtMatrixOption[];
  onCallback: (result: CompareTarget[]) => void;
};

/**
 * @desc UT Matrix 비교 대상 선택 — 행마다 구분(기준/비교대상/참조)을 직접 지정한다.
 *
 * 기준(MST)/비교대상(TGT)/참조(REF)는 전체에서 각 1건씩만 지정할 수 있고, 이미 다른
 * 행이 그 역할을 갖고 있으면 자동으로 해제된다. 참조는 선택 사항이며 비교 분석에는
 * 참여하지 않고 대상 목록에만 함께 저장된다.
 *
 * 화면 형태는 ref/html/ut_matrix_rpt_demo.html의 "UT Matrix 비교 대상 선택" 모달
 * (1236~1253줄)과 동일하다. 이전에는 그리드 행 드래그 순서로 구분이 정해졌는데,
 * 의도가 드러나지 않아 드롭다운 지정 방식으로 교체했다.
 */
const UtMatrixCompareTargetPopup = ({ open, close, matrices, onCallback }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();

  const [rows, setRows] = useState<UtMatrixOption[]>([]);
  /** utmId → 역할. 역할별로 최대 1건이라 값 기준으로도 역참조한다 */
  const [roles, setRoles] = useState<Record<string, MstTgtTpCd>>({});
  /** 공통코드 MST_TGT_TP_CD 의 코드명 (cd → nm) */
  const [mstTgtNames, setMstTgtNames] = useState<Record<string, string>>({});

  // 넘겨받은 순서를 그대로 후보 순서로 쓴다 (원본 배열은 건드리지 않는다)
  useEffect(() => {
    setRows((matrices || []).filter((m) => !!m?.utmId).map((m) => ({ ...m })));
    setRoles({});
  }, [matrices]);

  useEffect(() => {
    getCommonCodeNames('MST_TGT_TP_CD')
      .then((codes) => {
        const map: Record<string, string> = {};
        (codes || []).forEach((c) => {
          if (c?.cmnCd) map[c.cmnCd] = c.cmnCdNm || '';
        });
        setMstTgtNames(map);
      })
      .catch(() => setMstTgtNames({}));
  }, []);

  /** 표시 문구는 공통코드 우선, 조회 실패 시 상수의 기본값 */
  const roleLabel = (cd: MstTgtTpCd) =>
    mstTgtNames[cd] || MST_TGT_TYPES.find((x) => x.cd === cd)?.nm || cd;

  const setRole = (utmId: string, role: MstTgtTpCd | '') => {
    setRoles((prev) => {
      const next = { ...prev };
      if (role === '') {
        delete next[utmId];
        return next;
      }
      // 같은 역할을 이미 가진 행이 있으면 그 행에서 해제한다 (역할당 1건)
      Object.entries(next).forEach(([id, r]) => {
        if (r === role) delete next[id];
      });
      next[utmId] = role;
      return next;
    });
  };

  const pickedByRole = (role: MstTgtTpCd) => {
    const utmId = Object.entries(roles).find(([, r]) => r === role)?.[0];
    return utmId ? rows.find((m) => m.utmId === utmId) : undefined;
  };

  const hasBase = !!pickedByRole('MST');
  const hasTarget = !!pickedByRole('TGT');

  const handleConfirm = () => {
    if (!hasBase || !hasTarget) {
      openMessageBar({
        type: 'error',
        content: t(
          'ut.label.기준과 비교대상을 각각 1건씩 지정해 주세요.',
          '기준과 비교대상을 각각 1건씩 지정해 주세요.'
        ),
      });
      return;
    }
    // 기준 → 비교대상 → 참조 순서로 담아 배열 순서가 곧 SORT_ORD가 되게 한다
    const picked: CompareTarget[] = [];
    ROLE_ORDER.forEach((role) => {
      const hit = pickedByRole(role);
      if (hit) picked.push({ ...hit, mstTgtTpCd: role, sortOrd: picked.length + 1 });
    });
    onCallback(picked);
    close();
  };

  /*
    ⚠ disabled를 걸지 않는다 — IconButton.button은 아이콘을 ::before의 CSS mask로 그리는데
    IconSVG.tsx에 disabled 스타일이 없어서, MUI 기본 .Mui-disabled가 색을 덮어쓰면
    아이콘이 뭉개진다. 대신 handleConfirm에서 검증하고 안내 메시지를 띄운다.
  */
  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];

  return (
    <CustomDialog
      title={t('ut.label.UT Matrix 비교 대상 선택', 'UT Matrix 비교 대상 선택')}
      size="lg"
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <PickerRoot>
        <div className="picker-count">
          {t('com.label.총', '총')} {rows.length.toLocaleString()}
          {t('com.label.건', '건')}
        </div>

        <div className="picker-body">
          <table>
            <thead>
              <tr>
                <th>{t('ut.label.UT Matrix 번호', 'UT Matrix 번호')}</th>
                <th>{t('ut.label.UT Matrix 명', 'UT Matrix 명')}</th>
                <th style={{ width: 70 }}>{t('ut.label.버전', '버전')}</th>
                <th style={{ width: 120 }}>{t('ut.label.구분', '구분')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty">
                    {t('com.label.표시할 데이터가 없습니다.', '표시할 데이터가 없습니다.')}
                  </td>
                </tr>
              ) : (
                rows.map((m) => {
                  const role = roles[m.utmId] ?? '';
                  const cls = role ? role.toLowerCase() : '';
                  return (
                    <tr key={m.utmId} className={role ? `row-${cls}` : ''}>
                      <td className="center">{m.utmNo}</td>
                      <td>{m.utmNm}</td>
                      <td className="center">{m.verNo}</td>
                      <td className="center">
                        <select
                          className={`role-select${role ? ` role-${cls}` : ''}`}
                          value={role}
                          onChange={(e) => setRole(m.utmId, e.target.value as MstTgtTpCd | '')}
                        >
                          <option value="">{t('com.label.미선택', '미선택')}</option>
                          {ROLE_ORDER.map((cd) => (
                            <option key={cd} value={cd}>
                              {roleLabel(cd)}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </PickerRoot>
    </CustomDialog>
  );
};

export default UtMatrixCompareTargetPopup;
